import React, { useState, useEffect, useMemo } from 'react';

// Comps
import { CustomFields } from '@project-stories/ecommerce/Account/AccountFields/AccountFields';
import {InputField} from '@project-stories/elements/InputField/InputField';
import {Paragraph} from '@arctheme-components/elements/Paragraph/Paragraph';
import ButtonSpinner from '@arctheme-components/blocks/ButtonSpinner/ButtonSpinner';
import AccountSectionLayout from '@project-stories/ecommerce/Account/AccountSectionLayout';

// Hooks
import { useGetCustomerQuery } from '@src/hooks/useGetCustomerQuery';
import useCustomerUpdate from '@src/hooks/useCustomerUpdate';
import {Button} from "@src/stories/elements/Button/Button";

const AccountProfileForm = ({ section, signUpFields }) => {
  const {
    first_name_label = 'First Name',
    first_name_placeholder = 'Enter your first name',
    last_name_label = 'Last Name',
    last_name_placeholder = 'Enter your last name',
    email_label = 'Email',
    email_placeholder = 'Enter your email',
    button_label = 'Update Profile',
    button_style = 'primary',
    success_message = 'Profile updated successfully'
  } = section || {};

  // Sort custom fields
  const customSignUpFields = signUpFields?.sort((a, b) => {
    const orderA = a.field_order ? parseInt(a.field_order) : Infinity;
    const orderB = b.field_order ? parseInt(b.field_order) : Infinity;
    return orderA - orderB;
  });

  // Hooks
  const { fetchCustomer, customer } = useGetCustomerQuery();
  const {
    updateCustomer,
    loading: updateCustomerLoading,
    success: updateCustomerSuccess,
    error: updateCustomerError,
    fieldsWithError: updateCustomerFieldsWithError
  } = useCustomerUpdate();

  // States
  const initialDependenciesState = useMemo(() => {
    return Object.assign(
      {},
      {
        firstName: '',
        lastName: '',
        email: ''
      },
      ...customSignUpFields.map((field) => ({
        [field.metafield_key]: ''
      }))
    );
  }, [customer]);
  const [dependenciesState, setDependenciesState]: any = useState(initialDependenciesState);
  const [isUpdated, setIsUpdated] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Handle input change and update global dependency state
  const handleInputChange = (e) => {
    const { name, value, type: inputType, checked } = e.target;

    // For checkboxes, use the checked value, otherwise use the input value
    const newValue = inputType === 'checkbox' ? checked : value;

    console.log(`Input changed: ${name} = ${newValue}`);
    // Update the global dependency state
    updateDependencyState(name, newValue);
  };

  // Function to update the global dependencies state
  const updateDependencyState = (name, value) => {
    const newData = {
      ...dependenciesState,
      [name]: value
    };

    setDependenciesState(newData);
    toggleIsUpdated(newData);
  };

  const toggleIsUpdated = (newData) => {
    const updated = Object.keys(newData).some((key) => customer[key] !== newData[key]);

    setIsUpdated(updated);
  };

  const handleSubmit = (event: React.SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();

    console.log('Submitting form with data:', dependenciesState);
    updateCustomer(dependenciesState, (window as any).customer.id, customSignUpFields);
    setIsEditMode(false);
    setIsUpdated(false);
  };

  const handleEditToggle = () => {
    if (isEditMode) {
      // If currently in edit mode and clicking save, submit the form
      const form = document.getElementById('editForm') as HTMLFormElement;
      if (form) {
        form.requestSubmit();
      }
    } else {
      // Switch to edit mode
      setIsEditMode(true);
    }
  };

  // Fetch customer
  useEffect(() => {
    fetchCustomer((window as any).customer.id, customSignUpFields);
  }, []);

  // And then pre-fill the fields
  useEffect(() => {
    if (customer) {
      for (const key in customer) {
        console.log(key);
        if (key in dependenciesState) {
          setDependenciesState((prev) => ({
            ...prev,
            [key]: customer[key]
          }));
        }
      }
    }
  }, [customer]);

  // Reset edit mode when update is successful
  useEffect(() => {
    if (updateCustomerSuccess) {
      setIsEditMode(false);
      setIsUpdated(false);
    }
  }, [updateCustomerSuccess]);

  const showCustomFields = true;
  return (
      <AccountSectionLayout classes='hide-scrollbar'>

        <div className={'flex flex-col lg:flex-row flex-wrap gap-[41px] w-full'}>
          <div className={'flex flex-col w-full'}>
            <form
                id='editForm'
                onSubmit={handleSubmit}
                className='account-page__form pt-[16px]'
            >
              <div className='flex flex-col gap-y-4 pb-[40px]'>
                {/* First Name */}
                <InputField
                    type='text'
                    id='firstNameRegister'
                    name='firstName'
                    defaultValue={dependenciesState['firstName']}
          
                    eventHandlers={{
                        onChange: (e) => {
                            // Handle change event
                            handleInputChange(e);
                        }
                    }}
                    required={true}
                    disabled={!isEditMode}
                    label={first_name_label}
                    placeholder={first_name_placeholder}
                    hasError={updateCustomerFieldsWithError?.includes('firstName')}
                />

                {/* Last Name */}
                <InputField
                    type='text'
                    id='lastNameRegister'
                    name='lastName'
                    defaultValue={dependenciesState['lastName']}
                    eventHandlers={{
                        onChange: (e) => {
                            // Handle change event
                            handleInputChange(e);
                        }
                    }}
                    required={true}
                    disabled={!isEditMode}
                    label={last_name_label}
                    placeholder={last_name_placeholder}
                    hasError={updateCustomerFieldsWithError?.includes('lastName')}
                />

                {/* Email */}
                <InputField
                    type='email'
                    id='emailRegister'
                    name='email'
                    defaultValue={dependenciesState['email']}
            
                    eventHandlers={{
                        onChange: (e) => {
                            // Handle change event
                            handleInputChange(e);
                        }
                    }}
                    required={true}
                    disabled={!isEditMode}
                    label={email_label}
                    placeholder={email_placeholder}
                    hasError={updateCustomerFieldsWithError?.includes('email')}
                />

                {/* Custom Fields */}
                {showCustomFields && (
                    <CustomFields
                        customSignUpFields={customSignUpFields}
                        onChange={handleInputChange}
                        values={dependenciesState}
                        fieldsWithError={updateCustomerFieldsWithError}
                    />
                )}

                {updateCustomerSuccess && (
                    <Paragraph
                        type='text-center success'
                        text={success_message}
                    />
                )}

                {updateCustomerError && (
                    <Paragraph
                        type='errors text-center'
                        text={updateCustomerError}
                    />
                )}

                {/* Edit/Save Button */}
                <Button
                    className={`w-full ${button_style} h-[calc(3rem-2px)] text-base`}
                    onClick={handleEditToggle}
                    disabled={updateCustomerLoading || (isEditMode && !isUpdated)}
                    aria-label={isEditMode ? 'Save profile changes' : 'Edit profile'}
                    label={updateCustomerLoading ? 'Updating...' : isEditMode ? 'Save Changes' : 'Edit Profile'}
                />
              </div>
            </form>
          </div>

          {false && <div className={`flex flex-col w-full lg:max-w-[307px]`}>
            <h4>CONTACT US</h4>
            <p className={`b2 pt-4 lg:pt-1`}>Contact customer care for further assistance.</p>
            <p className="b2 pt-[17px]">
              P - <a href="tel:+61418114745" className="cursor-pointer">(+61) 418 114 745</a>
            </p>
            <p className="b2 pt-2">
              E - <a href="mailto:online@leemathews.com.au" className="cursor-pointer">online@leemathews.com.au</a>
            </p>
          </div>}
        </div>


      </AccountSectionLayout>
  );
};

export default AccountProfileForm;
